import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { orderKeys } from '../hooks';

import {
  createOrderWarrantyClaim,
  getOrderWarrantyClaim,
  listOrderWarrantyClaims,
  listWarrantyEligibleItems,
} from './api';
import type { ClaimWarrantyValues } from './types';

/** The order's Warranty Claims + the backend's eligibility verdict. Called by `OrderActionsMenu`,
 *  which mounts with Order Details, enabled in parallel with the replacement query, never chained
 *  (ADR-0023). */
export function useOrderWarrantyClaims(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.warrantyClaims(orderId),
    queryFn: () => listOrderWarrantyClaims(orderId),
    enabled: enabled && Boolean(orderId),
  });
}

/** One Warranty Claim's detail, for the Ticket Preview drawer. Takes an explicit `enabled` like the
 *  listing hook above rather than reading an empty-string sentinel, so a closed drawer that stays
 *  mounted never fetches (mirrors `useOrderReplacementRequest`). */
export function useOrderWarrantyClaim(orderId: string, claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.warrantyClaim(orderId, claimId),
    queryFn: () => getOrderWarrantyClaim(orderId, claimId),
    enabled: enabled && Boolean(orderId) && Boolean(claimId),
  });
}

/** Eligible line items + their eligible Unit numbers, fetched when the intake drawer opens rather
 *  than mounted with Order Details like the two queries above (mirrors `useReplacementEligibleItems`). */
export function useWarrantyEligibleItems(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.warrantyEligibleItems(orderId),
    queryFn: () => listWarrantyEligibleItems(orderId),
    enabled: enabled && Boolean(orderId),
  });
}

/** Raises a Warranty Claim against one Unit. Invalidates **both** order-scoped warranty queries: the
 *  claims listing (also refreshing a stale Ticket Preview, via prefix match) and the eligible items,
 *  since the claimed Unit is no longer eligible (AC 112444, ADR-0023). */
export function useCreateOrderWarrantyClaim(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: ClaimWarrantyValues) => createOrderWarrantyClaim(orderId, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderKeys.warrantyClaims(orderId) });
      void qc.invalidateQueries({ queryKey: orderKeys.warrantyEligibleItems(orderId) });
    },
  });
}
